import { I18n } from "@assets/strings";
import React, { useEffect, useState } from "react";
import styled, { css } from "styled-components";
import { useServices } from "@core/servicesContext";
import { CurrentProfile } from "@domain/currentProfile/currentProfile";
import { ProfileDraft } from "@domain/currentProfile/profileDraft";
import { useAsync } from "@utils/hooks";
import { LoadingButton } from "@ui/components/buttons/loadingButton";
import { SecondaryButton } from "@ui/components/buttons/secondaryButton";
import { TertiaryButton } from "@ui/components/buttons/tertiaryButton";
import { RoundedTextInput } from "@ui/components/inputs/textInput";
import { createToast } from "@ui/toasts/utils";

const MAX_DESCRIPTION_LENGTH = 80;
interface ProfileDescriptionEditorProps {
	initialProfile: CurrentProfile;
}

export const ProfileDescriptionEditor: React.FC<ProfileDescriptionEditorProps> = ({ initialProfile }) => {
	const { currentProfileService } = useServices();
	const [profile, setProfile] = useState<ProfileDraft>(initialProfile);
	const [editorActive, setEditorActive] = useState(false);
	const { run, loading } = useAsync(() => currentProfileService.saveProfileInfos(profile), [profile]);
	const descriptionLength = profile.description.length;
	const invalidDescription = descriptionLength > MAX_DESCRIPTION_LENGTH;
	useEffect(() => setProfile(initialProfile), [initialProfile]);

	return !editorActive ? (
		<AddDescriptionButton onClick={() => setEditorActive(true)}>
			{!profile.description ? I18n.t("profile.card.description.add") : null}
		</AddDescriptionButton>
	) : (
		<EditorContainer>
			<DescriptionInput
				value={profile.description}
				onChange={(e) => {
					const value = e.currentTarget.value;
					setProfile((current) => ({ ...current, description: value }));
				}}
				isInvalid={invalidDescription}
			/>
			<SizeCounter isInvalid={invalidDescription}>{`${descriptionLength}/${MAX_DESCRIPTION_LENGTH}`}</SizeCounter>
			<Actions>
				<SecondaryButton
					onClick={() => {
						setProfile((current) => ({ ...current, description: initialProfile.description }));
						setEditorActive(false);
					}}
				>
					{I18n.t("profile.card.description.cancel")}
				</SecondaryButton>
				<LoadingButton
					buttonType={"primary"}
					onClick={async () => {
						try {
							await run();
							setEditorActive(false);
						} catch {
							createToast(I18n.t("error"), I18n.t("profile.save.error"), "error");
						}
					}}
					loading={loading}
					disabled={invalidDescription}
				>
					{I18n.t("profile.card.description.confirm")}
				</LoadingButton>
			</Actions>
		</EditorContainer>
	);
};

const AddDescriptionButton = styled(TertiaryButton)`
	margin-top: 10px;
	color: ${({ theme }) => theme.colors.secondary};
`;

const EditorContainer = styled.div`
	width: 100%;
	margin-top: 20px;
`;

const DescriptionInput = styled(RoundedTextInput)<{ isInvalid?: boolean }>`
	width: 100%;
	padding: 10px 12px 9px;
	border-radius: 10px;
	font-size: 0.935rem;
	:focus {
		border: 1px solid ${({ theme }) => theme.colors.darkgray};
	}
	${({ isInvalid }) =>
		isInvalid &&
		css`
			border: 1px solid ${({ theme }) => theme.colors.red};
		`}
`;

const Actions = styled.div`
	display: flex;
	flex-direction: row;
	justify-content: center;

	> button {
		margin-top: 20px;
	}

	> :first-child {
		margin-right: 20px;
	}

	@media (max-width: 900px) {
		> button {
			padding-left: 30px;
			padding-right: 30px;
		}
	}
`;

const SizeCounter = styled.div.withConfig({
	shouldForwardProp: (prop) => prop !== "isInvalid",
})<{ isInvalid: boolean }>`
	text-align: right;
	color: ${({ theme, isInvalid }) => (isInvalid ? theme.colors.red : theme.colors.textTertiary)};
	font-size: 0.8125rem;
`;
